import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { NEW_USER_PROFILE_FLAG } from '@/hooks/useOwnProfileLink';

// Django selects the tab with `opentab=<code>`, the React profile with `tab=<id>`.
const DJANGO_TO_REACT_TAB = { perm: 'permissions' };

/**
 * Returns a builder for a user's profile URL.
 *
 * With the `new_user_profile_view` flag on it points to the React `/user?id=<id>`
 * page, otherwise to the Django `{tokkoBrokerHost}/user/<id>/details` one.
 * Single source of truth: use it instead of composing the URL by hand.
 *
 * @example
 * const getUserProfileLink = useUserProfileLink();
 * window.open(getUserProfileLink(agent.id), '_blank');
 * getUserProfileLink(agent.id, { opentab: 'perm' }); // with extra query params
 *
 * @returns {(id: number|string, params?: Object<string, string>) => string}
 *   Absolute URL; `params` are appended as extra query params. `opentab` is
 *   the Django tab param: with the flag on it is sent as `tab=<react tab id>`.
 */
function useUserProfileLink() {
  const isNewProfile = useSelector((state) =>
    state.AuthSlice?.permissions?.waffle_flags?.includes(NEW_USER_PROFILE_FLAG)
  );

  return useCallback(
    (id, params = {}) => {
      const url = isNewProfile
        ? new URL('/user', window.location.origin)
        : new URL(
            `/user/${id}/details`,
            localStorage.getItem('tokkoBrokerHost') || window.location.origin
          );

      if (isNewProfile) {
        url.searchParams.set('id', id);
      }
      Object.entries(params).forEach(([key, value]) => {
        if (isNewProfile && key === 'opentab') {
          url.searchParams.set('tab', DJANGO_TO_REACT_TAB[value] ?? value);
        } else {
          url.searchParams.set(key, value);
        }
      });

      return url.toString();
    },
    [isNewProfile]
  );
}

export default useUserProfileLink;
